import type { PublishedPostDto, PublishedPostsSort } from "@nlc/shared-types";

import dayjs from "dayjs";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useState } from "react";
import { TableBody } from "react-aria-components";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Loader } from "@/components/Loader";
import { SearchField, SearchInput } from "@/components/search-field";
import { SegmentedControl } from "@/components/segmented-control";
import { Cell, Column, Row, Table, TableHeader } from "@/components/table";
import { useDebounce } from "@/hooks/useDebounce";
import { useUserTimezone } from "@/hooks/useUserTimezone";

import type { InsightsFilters } from "./insightsFilters";

import { usePublishedPosts } from "../../hooks/usePublishedPosts";
import { SOCIAL_PLATFORM_LABELS } from "../socialPlatformLabels";
import { compareToAverage } from "./compareToAverage";
import { formatMetric } from "./formatMetric";
import { PostThumbnail } from "./overview/PostThumbnail";
import { POST_FORMAT_LABELS } from "./postFormatLabels";

const SORT_OPTIONS: Array<{ id: PublishedPostsSort; label: string }> = [
  { id: "views", label: "Most views" },
  { id: "interactions", label: "Most interactions" },
  { id: "newest", label: "Newest" },
  { id: "oldest", label: "Oldest" },
];

export function PostsAnalytics({
  filters,
  onSelectPost,
}: {
  filters: InsightsFilters;
  onSelectPost: (postId: number) => void;
}) {
  const { timezone } = useUserTimezone();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 300);
  const [sort, setSort] = useState<PublishedPostsSort>("views");
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    usePublishedPosts({ ...filters, search: debouncedSearch, sort, timezone });
  const posts = data?.pages.flatMap((page) => page.posts) ?? [];
  const firstPage = data?.pages[0];

  return (
    <div className="flex flex-col gap-lg p-xl">
      <div className="flex flex-col gap-xs">
        <h3 className="text-lg font-semibold text-text-primary">Your posts</h3>
        <p className="text-sm text-text-tertiary">
          Every post published in this period. Select one to see all of its
          numbers.
        </p>
      </div>
      <div className="flex flex-col gap-lg rounded-2xl border border-border-secondary bg-bg-primary p-2xl">
        <div className="flex flex-wrap items-center gap-lg">
          <SearchField
            aria-label="Search posts"
            className="min-w-[240px] flex-1"
            onChange={setSearch}
            value={search}
          >
            <SearchInput
              onClear={() => setSearch("")}
              placeholder="Search posts by caption or brand"
            />
          </SearchField>
          <SegmentedControl<PublishedPostsSort>
            aria-label="Sort posts"
            onChange={setSort}
            options={SORT_OPTIONS}
            value={sort}
          />
        </div>
        {isLoading ? (
          <Loader className="py-20" />
        ) : (
          <PostsTable
            averageViews={firstPage?.averageViews ?? null}
            onSelect={onSelectPost}
            posts={posts}
          />
        )}
        {firstPage && firstPage.total > 0 && (
          <div className="flex items-center justify-between gap-md">
            <span className="text-sm text-text-tertiary">
              Showing {posts.length} of {firstPage.total} posts
            </span>
            {hasNextPage && (
              <Button
                color="gray"
                isPending={isFetchingNextPage}
                onPress={() => fetchNextPage()}
                variant="secondary"
              >
                Show 10 more
              </Button>
            )}
          </div>
        )}
      </div>
      <p className="text-sm text-text-tertiary">
        Numbers come from Instagram and Facebook and refresh a few times a day.
        &ldquo;How it did&rdquo; compares a post&apos;s views with the average
        of the posts listed here.
      </p>
    </div>
  );
}

function PostsTable({
  averageViews,
  onSelect,
  posts,
}: {
  averageViews: null | number;
  onSelect: (postId: number) => void;
  posts: PublishedPostDto[];
}) {
  return (
    <div className="overflow-hidden [&_[role=row]]:cursor-pointer">
      <Table
        aria-label="Published posts"
        onRowAction={(key) => onSelect(Number(key))}
      >
        <TableHeader>
          <Column defaultWidth="4fr" id="post" isRowHeader minWidth={260}>
            Post
          </Column>
          <Column defaultWidth="1fr" id="publishedAt" minWidth={110}>
            Published
          </Column>
          <Column defaultWidth="1fr" id="views" minWidth={90}>
            Views
          </Column>
          <Column defaultWidth="1fr" id="interactions" minWidth={110}>
            Interactions
          </Column>
          <Column defaultWidth="1fr" id="performance" minWidth={150}>
            How it did
          </Column>
        </TableHeader>
        <TableBody
          items={posts}
          renderEmptyState={() => (
            <p className="p-3xl text-center text-sm text-text-tertiary">
              No published posts match these filters.
            </p>
          )}
        >
          {(post) => (
            <Row id={post.id}>
              <Cell>
                <div className="flex items-center gap-lg py-md">
                  <PostThumbnail className="size-12 rounded-md" post={post} />
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate font-semibold text-text-primary">
                      {post.name || post.designs[0]?.name || post.brand.name}
                    </span>
                    <span className="truncate text-text-tertiary">
                      {[
                        post.brand.name,
                        post.page
                          ? SOCIAL_PLATFORM_LABELS[post.page.platform]
                          : "Disconnected",
                        POST_FORMAT_LABELS[post.format],
                      ].join(" · ")}
                    </span>
                  </div>
                </div>
              </Cell>
              <Cell>
                {dayjs(post.publishedAt ?? post.scheduledAt).format("MMM D")}
              </Cell>
              <Cell>
                <span className="font-semibold text-text-primary">
                  {formatMetric(post.insights?.views)}
                </span>
              </Cell>
              <Cell>{formatMetric(post.insights?.totalInteractions)}</Cell>
              <Cell>
                <PerformanceBadge
                  averageViews={averageViews}
                  views={post.insights?.views ?? null}
                />
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function PerformanceBadge({
  averageViews,
  views,
}: {
  averageViews: null | number;
  views: null | number;
}) {
  const comparison = compareToAverage(views, averageViews);

  if (comparison === "above")
    return (
      <Badge isRounded size="small" variant="success">
        <ArrowUp className="size-3" />
        Above average
      </Badge>
    );
  if (comparison === "below")
    return (
      <Badge isRounded size="small" variant="error">
        <ArrowDown className="size-3" />
        Below average
      </Badge>
    );
  if (comparison === "about")
    return (
      <Badge isRounded size="small" variant="secondary">
        About average
      </Badge>
    );

  return <span className="text-text-quaternary">—</span>;
}
